import React, { useEffect, useMemo, useState } from "react";
import { API_BASE } from "../services/api";
import { awinApi } from "../services/externalPlatformApi";

const money = (value) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(Number(value || 0));

export default function AdminExternalSales() {
  const [sales, setSales] = useState([]);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const totals = useMemo(() => {
    return sales.reduce(
      (acc, sale) => {
        acc.saleAmount += Number(sale.sale_amount || 0);
        acc.awin += Number(sale.awin_commission || 0);
        acc.affiliate += Number(sale.affiliate_payout || 0);
        acc.hlt += Number(sale.hlt_payout || 0);
        return acc;
      },
      { saleAmount: 0, awin: 0, affiliate: 0, hlt: 0 }
    );
  }, [sales]);

  useEffect(() => {
    loadSales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  async function loadSales() {
    setLoading(true);
    setError("");
    try {
      const params = status ? { status, limit: 500 } : { limit: 500 };
      const payload = await awinApi.listSales(params);
      setSales(Array.isArray(payload?.sales) ? payload.sales : []);
    } catch {
      setError("Could not load external sales. The backend endpoint may not be live yet.");
    } finally {
      setLoading(false);
    }
  }

  const exportHref = `${API_BASE}${awinApi.exportSalesUrl(status ? { status } : {})}`;

  return (
    <div className="page space-y-6">
      <section className="card">
        <div className="md:flex md:items-end md:justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl md:text-4xl">External Sales</h1>
            <p className="mt-1 text-slate-300">
              MASTER revenue view for Awin and external affiliate sales.
            </p>
          </div>
          <div className="mt-4 flex flex-wrap gap-2 md:mt-0">
            <select className="input w-44" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All statuses</option>
              <option value="PENDING">Pending</option>
              <option value="CONFIRMED">Confirmed</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
            <a href={exportHref} className="btn-secondary" target="_blank" rel="noopener noreferrer">
              Export CSV
            </a>
          </div>
        </div>
        {error && (
          <div className="mt-3 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-red-200">
            {error}
          </div>
        )}
      </section>

      <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KPI label="Sale value" value={money(totals.saleAmount)} />
        <KPI label="Awin commission" value={money(totals.awin)} />
        <KPI label="Affiliate payouts" value={money(totals.affiliate)} />
        <KPI label="HLT net" value={money(totals.hlt)} />
      </section>

      {loading && <section className="card text-slate-300">Loading external sales...</section>}

      {!loading && sales.length === 0 && (
        <section className="card text-slate-300">No external sales have been recorded yet.</section>
      )}

      {!loading && sales.length > 0 && (
        <section className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-left text-sm">
              <thead className="border-b border-white/10 text-xs uppercase tracking-wider text-slate-400">
                <tr>
                  <th className="py-3 pr-4">Date</th>
                  <th className="py-3 pr-4">Affiliate</th>
                  <th className="py-3 pr-4">Partner</th>
                  <th className="py-3 pr-4">Sale</th>
                  <th className="py-3 pr-4">Awin</th>
                  <th className="py-3 pr-4">Affiliate</th>
                  <th className="py-3 pr-4">HLT</th>
                  <th className="py-3 pr-4">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {sales.map((sale) => (
                  <tr key={sale.id}>
                    <td className="py-3 pr-4 text-slate-300">
                      {sale.created_at ? new Date(sale.created_at).toLocaleDateString() : "TBC"}
                    </td>
                    <td className="py-3 pr-4 text-white">{sale.affiliate_code || "Unattributed"}</td>
                    <td className="py-3 pr-4 text-slate-300">{sale.partner_name}</td>
                    <td className="py-3 pr-4 text-slate-300">{money(sale.sale_amount)}</td>
                    <td className="py-3 pr-4 text-slate-300">{money(sale.awin_commission)}</td>
                    <td className="py-3 pr-4 text-slate-300">{money(sale.affiliate_payout)}</td>
                    <td className="py-3 pr-4 text-slate-300">{money(sale.hlt_payout)}</td>
                    <td className="py-3 pr-4">
                      <span className="badge">{sale.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

function KPI({ label, value }) {
  return (
    <div className="card">
      <div className="text-[11px] uppercase tracking-wider text-slate-400">{label}</div>
      <div className="mt-1 text-xl font-semibold text-white">{value}</div>
    </div>
  );
}
